import { useMutation, useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { queryClientAtom } from 'jotai-tanstack-query';
import axiosApiInstance from '../../../api/axiosClient';
import type { Campaign, CampaignDeliverable, DeliverablePlatform, DeliverablePostType } from './types';

const KEY = 'campaigns';

// Detail query stays on React-Query's context (not jotai-backed) because the
// ViewModal/EditModal mount/unmount per-id and don't need global subscription.
export const useCampaignQuery = (id: number | string | undefined) =>
    useQuery<Campaign>({
        queryKey: [KEY, 'detail', id],
        enabled: id !== undefined && id !== null && String(id) !== 'new',
        queryFn: async () =>
            (await axiosApiInstance.get<Campaign>(`/api/campaigns/items/${id}`)).data,
        cacheTime: 0,
        staleTime: 0
    });

// Mutations invalidate via the jotai-tanstack-query client so campaignsAtom
// subscribers refetch automatically. This matches the proven creators pattern
// where atomWithQuery + queryClientAtom keep the list in sync after writes.

export const useCreateCampaign = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async (campaign: Partial<Campaign>) =>
            (await axiosApiInstance.post<Campaign>('/api/campaigns/items', campaign)).data,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [KEY] });
        }
    });
};

export const useUpdateCampaign = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({ id, ...campaign }: Partial<Campaign> & { id: number }) =>
            (await axiosApiInstance.put(`/api/campaigns/items/${id}`, campaign)).data,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [KEY] });
        }
    });
};

export const useDeleteCampaign = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async (id: number) =>
            (await axiosApiInstance.delete(`/api/campaigns/items/${id}`)).data,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [KEY] });
        }
    });
};

export const useAppendCreators = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({ id, creatorIds }: { id: number; creatorIds: number[] }) =>
            (await axiosApiInstance.post(`/api/campaigns/items/${id}/creators`, { creator_ids: creatorIds })).data,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [KEY] });
        }
    });
};

/** Upsert per-creator budget / comments for a campaign–creator pair. */
export const useUpdateCreatorMeta = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({
            campaignId,
            creatorId,
            budget,
            comments,
            approved,
            pillar,
        }: {
            campaignId: number;
            creatorId: number;
            budget?: number | null;
            comments?: string;
            approved?: boolean | null;
            pillar?: string;
        }) =>
            (await axiosApiInstance.patch(
                `/api/campaigns/items/${campaignId}/creators/${creatorId}/meta`,
                { budget: budget ?? null, comments: comments ?? '', approved: approved ?? null, pillar: pillar ?? '' },
            )).data,
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'detail', vars.campaignId] });
        },
    });
};

// --- Campaign deliverable template hooks ------------------------------------

export const useAddCampaignDeliverable = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({
            campaignId,
            platform,
            post_type,
            quantity,
        }: { campaignId: number; platform: DeliverablePlatform; post_type: DeliverablePostType; quantity: number }) =>
            (await axiosApiInstance.post<CampaignDeliverable>(
                `/api/campaigns/items/${campaignId}/deliverables`,
                { platform, post_type, quantity },
            )).data,
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'detail', vars.campaignId] });
        },
    });
};

export const useDeleteCampaignDeliverable = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({ campaignId, deliverableId }: { campaignId: number; deliverableId: number }) =>
            (await axiosApiInstance.delete(
                `/api/campaigns/items/${campaignId}/deliverables/${deliverableId}`,
            )).data,
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'detail', vars.campaignId] });
        },
    });
};

// --- Per-creator deliverable hooks ------------------------------------------

export const useAddCreatorDeliverable = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({
            campaignId,
            creatorId,
            platform,
            post_type,
            quantity,
        }: { campaignId: number; creatorId: number; platform: DeliverablePlatform; post_type: DeliverablePostType; quantity: number }) =>
            (await axiosApiInstance.post(
                `/api/campaigns/items/${campaignId}/creators/${creatorId}/deliverables`,
                { platform, post_type, quantity },
            )).data,
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'detail', vars.campaignId] });
        },
    });
};

export const useUpdateCreatorDeliverable = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({
            campaignId,
            creatorId,
            deliverableId,
            completed,
            quantity,
        }: { campaignId: number; creatorId: number; deliverableId: number; completed: number; quantity: number }) =>
            (await axiosApiInstance.patch(
                `/api/campaigns/items/${campaignId}/creators/${creatorId}/deliverables/${deliverableId}`,
                { completed, quantity },
            )).data,
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'detail', vars.campaignId] });
        },
    });
};

export const useDeleteCreatorDeliverable = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({
            campaignId,
            creatorId,
            deliverableId,
        }: { campaignId: number; creatorId: number; deliverableId: number }) =>
            (await axiosApiInstance.delete(
                `/api/campaigns/items/${campaignId}/creators/${creatorId}/deliverables/${deliverableId}`,
            )).data,
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'detail', vars.campaignId] });
        },
    });
};
